import { lookupDomainsApi } from '../queries/domainLookup';
import { getThreatUrl } from '../utils/domainUtils';
import { getTakeDownListApi } from '../queries/takedowns';
import { fetchGSBHistoryApi } from './gsbHistoryApi';
import { supabase } from './supabaseService';
import { fetchDomainData, DomainData } from './ctiGraphService';
import { REACT_APP_FN_TABLE } from '../utils/envVar';

// Domain 360 (WHEEL-318): one domain in, every platform surface checked.
// Each lookup is independent so the dialog can stream results per source.

export type Domain360SectionStatus = 'found' | 'none' | 'error';

export interface Domain360ThreatHit {
  threat_name: string;
  // Full URL for URL threats (threat_name is only the registered domain).
  threat_url: string;
  threat_id: number;
  protected_asset_id: number;
  protected_asset_name?: string;
  company_id: number;
  status?: string;
}

export interface Domain360TakedownHit {
  id: string;
  url?: string;
  status?: string;
  updated?: string;
}

export interface Domain360GsbHit {
  id?: number;
  url?: string;
  status?: string;
  created_on?: string;
}

export interface Domain360FnHit {
  id: number | string;
  domain?: string;
  review_status?: string;
  source?: string;
}

export interface Domain360Result<T> {
  status: Domain360SectionStatus;
  total: number;
  items: T[];
  // Threats only: the lookup endpoint caps each domain at 500 threats.
  truncated?: boolean;
  error?: string;
}

const SECTION_PAGE_SIZE = 25;
const MAX_ITEMS = 3;

const asError = (err: any): string =>
  err?.message || 'Lookup failed — source unavailable';

/**
 * Threat lookup via the exact-match lookup endpoint (WHEEL-403): one request,
 * all companies, all time. It replaced a one-request-per-year sweep over the
 * threats list endpoint that was slow and often timed out.
 */
export const lookupThreats = async (
  domain: string,
  signal?: AbortSignal,
): Promise<Domain360Result<Domain360ThreatHit>> => {
  try {
    const res = await lookupDomainsApi([domain], signal);
    const result = res?.results?.[0];
    const items: Domain360ThreatHit[] = (result?.threats ?? []).map(threat => ({
      threat_name: threat.threat_name,
      threat_url: getThreatUrl(threat),
      threat_id: threat.threat_id,
      protected_asset_id: threat.protected_asset_id,
      protected_asset_name: threat.protected_asset_name,
      company_id: threat.company_id,
      status: threat.identification_status?.value,
    }));
    return {
      status: items.length > 0 ? 'found' : 'none',
      total: items.length,
      truncated: Boolean(result?.truncated),
      items: items.slice(0, MAX_ITEMS),
    };
  } catch (err: any) {
    return { status: 'error', total: 0, items: [], error: asError(err) };
  }
};

export const lookupTakedowns = async (
  domain: string,
  signal?: AbortSignal,
): Promise<Domain360Result<Domain360TakedownHit>> => {
  try {
    const res = await getTakeDownListApi(
      {
        threat_domain: domain,
        target_domain: '',
        page: 1,
        size: SECTION_PAGE_SIZE,
        takedown_type: [],
        status_names: [],
        company_id: [],
      },
      signal,
    );
    const items = (res?.data?.items ?? []).map((takedown: any) => ({
      id: takedown.id,
      url: takedown.value?.value || takedown.attack_url || takedown.url,
      status: takedown.status?.name || takedown.status,
      updated: takedown.updated,
    }));
    return {
      status: items.length > 0 ? 'found' : 'none',
      total: res?.data?.total ?? items.length,
      items: items.slice(0, MAX_ITEMS),
    };
  } catch (err: any) {
    return { status: 'error', total: 0, items: [], error: asError(err) };
  }
};

export const lookupGsbHistory = async (
  domain: string,
): Promise<Domain360Result<Domain360GsbHit>> => {
  try {
    const res = await fetchGSBHistoryApi({
      page: 1,
      size: SECTION_PAGE_SIZE,
      url: domain,
    } as any);
    const items = (res?.data?.items ?? []).map((entry: any) => ({
      id: entry.id,
      url: entry.url,
      status: entry.status,
      created_on: entry.created_on,
    }));
    return {
      status: items.length > 0 ? 'found' : 'none',
      total: res?.data?.total ?? items.length,
      items: items.slice(0, MAX_ITEMS),
    };
  } catch (err: any) {
    return { status: 'error', total: 0, items: [], error: asError(err) };
  }
};

export const lookupFnCandidates = async (
  domain: string,
): Promise<Domain360Result<Domain360FnHit>> => {
  try {
    // Content-Detection / Matrix-Smith candidates leave fn_url.domain null and
    // carry the domain in fn_url.full_url (and sld) instead, so match across
    // all three — matching only `domain` misses those candidates entirely.
    // `*` is PostgREST's ilike wildcard inside an or() filter string.
    const escaped = domain.replace(/[(),]/g, '');
    const { data, count, error } = await supabase
      .from(REACT_APP_FN_TABLE)
      .select('id, review_status, data_source, fn_url', { count: 'exact' })
      .or(
        [
          `fn_url->>domain.ilike.*${escaped}*`,
          `fn_url->>full_url.ilike.*${escaped}*`,
          `fn_url->>sld.ilike.*${escaped}*`,
        ].join(','),
      )
      .limit(SECTION_PAGE_SIZE);
    if (error) {
      return { status: 'error', total: 0, items: [], error: error.message };
    }
    const items = (data ?? []).map((candidate: any) => ({
      id: candidate.id,
      domain:
        candidate.fn_url?.domain ||
        candidate.fn_url?.full_url ||
        candidate.fn_url?.sld,
      review_status: candidate.review_status,
      source: candidate.data_source,
    }));
    return {
      status: items.length > 0 ? 'found' : 'none',
      total: count ?? items.length,
      items: items.slice(0, MAX_ITEMS),
    };
  } catch (err: any) {
    return { status: 'error', total: 0, items: [], error: asError(err) };
  }
};

export interface Domain360CtiResult {
  status: Domain360SectionStatus;
  score?: number;
  scoreDate?: string;
  error?: string;
}

export const lookupCtiScore = async (
  domain: string,
): Promise<Domain360CtiResult> => {
  try {
    const res = await fetchDomainData(domain);
    const data: DomainData | undefined = res?.data;
    if (!res?.status || !data) {
      return { status: 'none' };
    }
    return {
      status: 'found',
      score: data.domainScore,
      scoreDate: data.scoreDate,
    };
  } catch (err: any) {
    return { status: 'error', error: asError(err) };
  }
};

/** External pivot links, domain-prefilled (same targets as ExternalToolList). */
export const domainPivotLinks = (domain: string) => [
  {
    label: 'urlscan.io',
    href: `https://urlscan.io/search/#${encodeURIComponent(domain)}`,
  },
  {
    label: 'VirusTotal',
    href: `https://www.virustotal.com/gui/domain/${encodeURIComponent(domain)}`,
  },
  { label: 'crt.sh', href: `https://crt.sh/?q=${encodeURIComponent(domain)}` },
  {
    label: 'Wayback',
    href: `https://web.archive.org/web/*/${encodeURIComponent(domain)}`,
  },
  {
    label: 'Whois',
    href: `https://www.whois.com/whois/${encodeURIComponent(domain)}`,
  },
];
